// frontend/components/HrEmployeeVacationsTab.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchMyVacationSummary,
  fetchMyLeaveRequests,
  createLeaveRequest,
} from "../features/hr/hrThunks.js";
import { Sun, Plane, CalendarDays, FilePlus2, Clock } from "lucide-react";
import "../styles/hr/hremployeevacations.css";

const getErrorText = (errorValue) => {
  if (!errorValue) return null;
  if (typeof errorValue === "string") return errorValue;
  return errorValue?.detail || "Ha ocurrido un error.";
};

const parseLocalDateFromYMD = (ymd) => {
  if (!ymd) return null;
  const parts = String(ymd).split("-");
  if (parts.length !== 3) return null;

  const year = Number(parts[0]);
  const month = Number(parts[1]);
  const day = Number(parts[2]);

  if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) {
    return null;
  }

  return new Date(year, month - 1, day);
};

const formatDate = (ymd) => {
  const date = parseLocalDateFromYMD(ymd);
  if (!date) return "—";
  return date.toLocaleDateString("es-ES", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const HrEmployeeVacationsTab = () => {
  const dispatch = useDispatch();
  const currentYear = new Date().getFullYear();

  const myVacationSummary = useSelector((state) => state.hr.myVacationSummary);
  const loadingVacationSummary = useSelector((state) => state.hr.loadingVacationSummary);
  const errorVacationSummary = useSelector((state) => state.hr.errorVacationSummary);

  const myLeaveRequests = useSelector((state) => state.hr.myLeaveRequests) || [];
  const loadingMyLeaveRequests = useSelector((state) => state.hr.loadingMyLeaveRequests);
  const errorMyLeaveRequests = useSelector((state) => state.hr.errorMyLeaveRequests);

  const loadingCreateLeaveRequest = useSelector((state) => state.hr.loadingCreateLeaveRequest);
  const errorCreateLeaveRequest = useSelector((state) => state.hr.errorCreateLeaveRequest);

  const [leaveType, setLeaveType] = useState("vacation");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [reason, setReason] = useState("");

  const [formError, setFormError] = useState(null);
  const [successMessage, setSuccessMessage] = useState("");

  useEffect(() => {
    dispatch(fetchMyVacationSummary({ year: currentYear }));
    dispatch(fetchMyLeaveRequests());
  }, [dispatch, currentYear]);

  const totalDays = myVacationSummary?.annual_vacation_days ?? 0;
  const usedDays = myVacationSummary?.used_vacation_days ?? 0;
  const remainingDays =
    myVacationSummary?.remaining_vacation_days ?? Math.max(totalDays - usedDays, 0);

  const sortedRequests = useMemo(() => {
    const copy = Array.isArray(myLeaveRequests) ? myLeaveRequests.slice() : [];
    copy.sort((a, b) => {
      const startA = String(a?.start_date || "");
      const startB = String(b?.start_date || "");
      if (startA !== startB) return startB.localeCompare(startA);
      const idA = Number(a?.id || 0);
      const idB = Number(b?.id || 0);
      return idB - idA;
    });
    return copy;
  }, [myLeaveRequests]);

  const renderStatusLabel = (status) => {
    if (status === "pending") return "Pendiente";
    if (status === "approved") return "Aprobada";
    if (status === "rejected") return "Rechazada";
    if (status === "cancelled") return "Cancelada";
    return status || "—";
  };

  const getStatusClass = (status) => {
    if (status === "pending") return "hr-status-badge hr-status-badge--pending";
    if (status === "approved") return "hr-status-badge hr-status-badge--approved";
    if (status === "rejected") return "hr-status-badge hr-status-badge--rejected";
    if (status === "cancelled") return "hr-status-badge hr-status-badge--cancelled";
    return "hr-status-badge";
  };

  const renderLeaveTypeLabel = (type) => {
    if (type === "vacation") return "Vacaciones";
    if (type === "day_off") return "Día libre";
    if (type === "sick_leave") return "Baja médica";
    if (type === "other") return "Otros";
    return type || "—";
  };

  const handleSubmitLeaveRequest = async (event) => {
    event.preventDefault();
    setSuccessMessage("");
    setFormError(null);

    if (!startDate || !endDate) {
      setFormError("Selecciona fecha de inicio y fecha de fin.");
      return;
    }

    if (endDate < startDate) {
      setFormError("La fecha de fin no puede ser anterior a la fecha de inicio.");
      return;
    }

    const resultAction = await dispatch(
      createLeaveRequest({
        leaveType,
        startDate,
        endDate,
        reason,
      })
    );

    const isFulfilled = createLeaveRequest?.fulfilled?.match
      ? createLeaveRequest.fulfilled.match(resultAction)
      : String(resultAction.type || "").endsWith("fulfilled");

    if (isFulfilled) {
      setSuccessMessage("Solicitud enviada correctamente.");
      setStartDate("");
      setEndDate("");
      setReason("");

      dispatch(fetchMyLeaveRequests());
      dispatch(fetchMyVacationSummary({ year: currentYear }));
    } else {
      const errorText = getErrorText(resultAction?.payload) || getErrorText(resultAction?.error);
      setFormError(errorText || "No se pudo enviar la solicitud.");
    }
  };

  const createErrorText = getErrorText(errorCreateLeaveRequest);
  const summaryErrorText = getErrorText(errorVacationSummary);
  const listErrorText = getErrorText(errorMyLeaveRequests);

  return (
    <div className="hr-vacations-tab">
      {/* Header */}
      <div className="hr-vacations-header-card">
        <div className="hr-vacations-header-left">
          <p className="hr-vacations-header-year">Vacaciones {currentYear}</p>
          <h2 className="hr-vacations-header-title">Vacaciones y ausencias</h2>
          <p className="hr-vacations-header-subtitle">
            Consulta tus días disponibles y solicita vacaciones o ausencias puntuales en un solo lugar.
          </p>
        </div>

        <div className="hr-vacations-header-right">
          <div className="hr-vacations-header-circle">
            <Sun className="hr-vacations-header-icon" aria-hidden="true" />
            <span className="hr-vacations-header-days">
              {loadingVacationSummary ? "…" : remainingDays}
            </span>
            <span className="hr-vacations-header-label">días libres</span>
          </div>
        </div>
      </div>

      {/* Métricas + Formulario */}
      <div className="hr-vacations-grid">
        <div className="hr-vacations-metrics">
          <div className="hr-metric-card hr-metric-card--primary">
            <div className="hr-metric-top">
              <span className="hr-metric-label">Días restantes</span>
              <Sun className="hr-metric-icon" aria-hidden="true" />
            </div>
            <div className="hr-metric-value">
              {loadingVacationSummary ? "…" : remainingDays}
              <span className="hr-metric-unit">días</span>
            </div>
            <p className="hr-metric-helper">Días que aún puedes disfrutar este año.</p>
          </div>

          <div className="hr-metric-card">
            <div className="hr-metric-top">
              <span className="hr-metric-label">Días usados</span>
              <Plane className="hr-metric-icon" aria-hidden="true" />
            </div>
            <div className="hr-metric-value">
              {loadingVacationSummary ? "…" : usedDays}
              <span className="hr-metric-unit">días</span>
            </div>
            <p className="hr-metric-helper">Vacaciones ya disfrutadas en {currentYear}.</p>
          </div>

          <div className="hr-metric-card">
            <div className="hr-metric-top">
              <span className="hr-metric-label">Días totales</span>
              <CalendarDays className="hr-metric-icon" aria-hidden="true" />
            </div>
            <div className="hr-metric-value">
              {loadingVacationSummary ? "…" : totalDays}
              <span className="hr-metric-unit">días</span>
            </div>
            <p className="hr-metric-helper">Política anual de vacaciones asignada.</p>
          </div>
        </div>

        <div className="hr-vacations-request-card">
          <h3 className="hr-info-title">
            <FilePlus2 className="hr-info-icon" aria-hidden="true" />
            Solicitar ausencia
          </h3>

          <form noValidate onSubmit={handleSubmitLeaveRequest} className="hr-form">
            <div className="hr-form-row">
              <label>Tipo de ausencia</label>
              <select value={leaveType} onChange={(event) => setLeaveType(event.target.value)}>
                <option value="vacation">Vacaciones</option>
                <option value="day_off">Día libre</option>
                <option value="sick_leave">Baja médica</option>
                <option value="other">Otros</option>
              </select>
            </div>

            <div className="hr-form-row hr-form-row--inline">
              <div className="hr-form-col">
                <label>Fecha inicio</label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(event) => setStartDate(event.target.value)}
                />
              </div>

              <div className="hr-form-col">
                <label>Fecha fin</label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(event) => setEndDate(event.target.value)}
                  min={startDate || undefined}
                />
              </div>
            </div>

            <div className="hr-form-row">
              <label>
                Motivo <span className="hr-form-optional">(opcional)</span>
              </label>
              <textarea
                value={reason}
                onChange={(event) => setReason(event.target.value)}
                rows={3}
                placeholder="Ej. Vacaciones de verano, cita médica, asuntos personales..."
              />
            </div>

            <button
              type="submit"
              className="hr-button hr-button--primary hr-vacations-submit-btn"
              disabled={loadingCreateLeaveRequest}
            >
              <Plane className="hr-button-icon" aria-hidden="true" />
              {loadingCreateLeaveRequest ? "Enviando solicitud..." : "Enviar solicitud"}
            </button>
          </form>

          {formError ? <div className="hr-error-text">Error: {formError}</div> : null}
          {createErrorText ? (
            <div className="hr-error-text">Error al enviar solicitud: {createErrorText}</div>
          ) : null}
          {successMessage ? <div className="hr-success-text">{successMessage}</div> : null}
          {summaryErrorText ? (
            <div className="hr-error-text small">
              No se pudo cargar el resumen de vacaciones: {summaryErrorText}
            </div>
          ) : null}
        </div>
      </div>

      {/* Historial */}
      <div className="hr-vacations-requests-card">
        <h3 className="hr-info-title">
          <Clock className="hr-info-icon" aria-hidden="true" />
          Historial de solicitudes
        </h3>

        {loadingMyLeaveRequests ? (
          <div className="hr-vacations-empty">Cargando solicitudes...</div>
        ) : listErrorText ? (
          <div className="hr-error-text">Error: {listErrorText}</div>
        ) : sortedRequests.length === 0 ? (
          <div className="hr-vacations-empty">Aún no has registrado ninguna solicitud.</div>
        ) : (
          <div className="hr-vacations-table-wrapper">
            <table className="hr-table">
              <thead>
                <tr>
                  <th>Tipo</th>
                  <th>Inicio</th>
                  <th>Fin</th>
                  <th>Días</th>
                  <th>Estado</th>
                </tr>
              </thead>
              <tbody>
                {sortedRequests.map((request) => (
                  <tr key={request.id}>
                    <td>{renderLeaveTypeLabel(request.leave_type)}</td>
                    <td>{formatDate(request.start_date)}</td>
                    <td>{formatDate(request.end_date)}</td>
                    <td>{request.total_days ?? "—"}</td>
                    <td>
                      <span className={getStatusClass(request.status)}>
                        {renderStatusLabel(request.status)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default HrEmployeeVacationsTab;